import { create } from 'zustand';

export type Theme = 'dark' | 'light';

const LS_THEME = 'ta_theme';
const LS_SIDEBAR = 'ta_sidebar';

function readTheme(): Theme {
  // Product decision (2026-05): the authenticated app is light by default
  // (matches the already-light /login page from commit d8e64c6). The
  // server-side `users.theme` default is migrated to `light` in
  // 20260528000000_user_theme_default_light; this client default is the
  // pre-/me fallback (used before the React-Query /me cache resolves on
  // first render, and on environments without localStorage e.g. SSR).
  if (typeof localStorage === 'undefined') return 'light';
  const v = localStorage.getItem(LS_THEME);
  return v === 'dark' ? 'dark' : 'light';
}

function readSidebar(): boolean {
  if (typeof localStorage === 'undefined') return true;
  return localStorage.getItem(LS_SIDEBAR) !== 'collapsed';
}

function applyTheme(theme: Theme): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
}

interface UIState {
  theme: Theme;
  sidebarExpanded: boolean;
  notifications: { unreadCount: number };
  setTheme(theme: Theme): void;
  toggleTheme(): void;
  toggleSidebar(): void;
  setSidebarExpanded(expanded: boolean): void;
  setUnreadCount(count: number): void;
}

// Theme state is single-source-of-truth at the <html data-theme> attribute.
// The store reads localStorage once at module init, writes back on change.
// Default for new users is `light` per spec/00 §0.2 (product decision
// 2026-05). Server-side `users.theme` default is matched by migration
// 20260528000000_user_theme_default_light. Authoritative source after
// login is `GET /me`.theme, applied into this store by `useMeQuery`.
export const useUIStore = create<UIState>((set, get) => {
  const initialTheme = readTheme();
  applyTheme(initialTheme);
  return {
    theme: initialTheme,
    sidebarExpanded: readSidebar(),
    notifications: { unreadCount: 0 },
    setTheme: (theme) => {
      applyTheme(theme);
      if (typeof localStorage !== 'undefined') localStorage.setItem(LS_THEME, theme);
      set({ theme });
    },
    toggleTheme: () => {
      const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
      get().setTheme(next);
    },
    toggleSidebar: () => {
      const next = !get().sidebarExpanded;
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LS_SIDEBAR, next ? 'expanded' : 'collapsed');
      }
      set({ sidebarExpanded: next });
    },
    setSidebarExpanded: (expanded) => {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(LS_SIDEBAR, expanded ? 'expanded' : 'collapsed');
      }
      set({ sidebarExpanded: expanded });
    },
    setUnreadCount: (count) => set({ notifications: { unreadCount: count } }),
  };
});
